import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { showTypeProfile } from '../lib/showTypes';
import { sortPeopleNames } from '../lib/people';
import { withCardIcon } from '../lib/uiIcons';
import { apiUpdateChecklist, apiGetShowChecklists, apiSetMyChecklistItemDone, type ChecklistDTO, type ChecklistItemDTO } from '../api';
import {
  apiGetShowRoadmap, apiGetShowContacts, apiGetShowDistances, apiGetShowFlights,
  apiGetShowTechNotes, apiGetShowMiscNotes, apiGetShowCategorizedNotes,
  type RoadmapItemDTO, type ShowContactDTO, type DistanceDTO, type MemberFlightDTO,
  type ShowTechNoteDTO, type ShowMiscNoteDTO, type ShowCategorizedNoteDTO,
  type GuestDTO, type AccommodationDTO, type RoomingDTO, type VenueDTO,
} from '../api';
import {
  createDefaultDaysheetLayout,
  mergeDaysheetLayouts,
  type DaysheetLayout, type DaysheetSectionId,
} from '../lib/daysheetLayout';

/**
 * DaysheetViewer — read-only consolidated view of a show, mirroring the
 * composition of the daysheet PDF.
 *
 * ARCHITECTURE (the core of this feature): the viewer consumes the SAME
 * `layout` object as the PDF generator (buildDaysheetDoc in src/lib/daysheet.ts).
 * It iterates `layout.sections` in order and renders each section via a
 * per-id renderer. A section id with NO registered renderer falls back to a
 * generic block, so a section added to DEFAULT_SECTION_ORDER tomorrow can reach
 * the screen WITHOUT any viewer change — the invariance criterion, which since
 * reversal (2) below reads: a new section appears IFF it is `enabled` AND
 * carries content. Not the visual polish.
 *
 * WHAT THE SCREEN SHOWS — the TWO reversals of 2026-10-17, dated and distinct,
 * so a future reader can tell which rule produced a given line:
 *
 *   (1) 2026-10-17 — THE EMPTY IS NOT SHOWN. Until then a section with no data
 *       still printed its heading plus a « Nothing to show yet » placeholder
 *       « so the composition is visible »; from now on an empty section
 *       (heading included) is not rendered at all.
 *
 *   (2) 2026-10-17 — THE `enabled` FLAG GOVERNS THE SCREEN TOO. Until then the
 *       viewer followed ONLY the order of `layout.sections` and deliberately
 *       ignored the print flag (a section disabled for the PDF still appeared on
 *       screen when it had content). The editor's toggle is worded generically
 *       (« Enable/disable »), so it governs BOTH surfaces: a disabled section is
 *       now absent from the screen exactly as it is absent from the PDF. The
 *       single constant `followEnabledForScreen` that used to encode the old
 *       choice is GONE — a flag that is always read as `true` is dead code.
 *       Alternative rejected: keeping the constant at `true`.
 *
 * The rules compose:
 *   - `enabled:false`                      ⇒ INVISIBLE (screen and PDF), whatever the data;
 *   - `enabled:true` + no data             ⇒ INVISIBLE (no orphan heading, no placeholder);
 *   - `enabled:true` + data (a renderer body, or a filled free-text field) ⇒ VISIBLE.
 * The ORDER is still `layout.sections`, and a section absent from an older saved
 * layout is still exposed by the defaults merge (`mergeDaysheetLayouts`) — do not
 * break that.
 *
 * NOT to be confused with the TRANSPORT: it is shown in the viewer header but is
 * DELIBERATELY absent from the printed daysheet (user choice B) — leave it alone.
 * Guard: src/components/__tests__/DaysheetViewer.invariance.test.tsx.
 */

// Transport label mapping — same shape as the old ShowDaySummary (option B).
// PRODUCT DECISION: the transport is shown on SCREEN in the viewer header but is
// DELIBERATELY absent from the printed daysheet — a daysheet goes to the promoter,
// and transport is not a daysheet field (it lives on the show, not in the layout).
const TRANSPORT_LABEL: Record<string, string> = {
  van: '🚐 Van',
  tourbus: '🚌 Tourbus',
  train: '🚆 Train',
  plane: '✈️ Plane',
};

interface Props {
  /** The show's form state (already loaded by ShowDetailPage — not re-fetched). */
  form: Record<string, any>;
  /** The show's venue (resolved from form.venue_id by the parent). */
  venue?: Partial<VenueDTO> | null;
  /** The show's numeric id (null for a new show). */
  realId: string | null;
  /** The band's display name (header section). */
  bandName?: string;
  /** The show's guests (already loaded by ShowDetailPage). */
  guests: GuestDTO[];
  /** The show's accommodations (already loaded by ShowDetailPage). */
  accommodations: AccommodationDTO[];
  /** The show's rooming rows (already loaded by ShowDetailPage). */
  rooming: RoomingDTO[];
  /** The band's members (for name resolution in flights / rooming). */
  members: { id: string; name: string; officialName?: string }[];
  /** Photo « document » de la salle (data URL déjà résolue) + légende. Absente ⇒
   *  la section venue-photo est invisible, comme dans le PDF. */
  venuePhoto?: { dataUrl?: string; caption?: string } | null;
  /** Qui est sur la route pour cette date (nom affiché + position), résolu par le
   *  parent via `crewRowsFromAssignments` — contrat nom/position partagé avec le
   *  PDF (src/lib/people.ts). Vide ⇒ section « BAND AND CREW » invisible. */
  people?: Array<{ name: string; position?: string }>;
  /** The SAME effective daysheet layout the PDF consumes. */
  layout: DaysheetLayout | null;
  /** Capability-aware write gate — controls the Edit button. */
  canWrite: boolean;
  /** The signed-in user's linked team_member id (Checklists V2-B): a
   *  non-editor viewer sees only HIS assigned checklist items. */
  myMemberId?: string | null;
  /** Switch the parent back to the full editing form + tabs. */
  onEdit: () => void;
}

export default function DaysheetViewer({
  form, venue, realId, bandName, guests, accommodations, rooming, members, people, venuePhoto, layout, canWrite, myMemberId: myMemberIdProp, onEdit,
}: Props) {
  const { t } = useTranslation();

  // ---- Data the parent does NOT already hold — fetched here (read-only GETs) ----
  const [roadmap, setRoadmap] = useState<RoadmapItemDTO[]>([]);
  const [contacts, setContacts] = useState<ShowContactDTO[]>([]);
  const [distances, setDistances] = useState<DistanceDTO[]>([]);
  const [flights, setFlights] = useState<MemberFlightDTO[]>([]);
  const [techNotes, setTechNotes] = useState<ShowTechNoteDTO[]>([]);
  const [miscNotes, setMiscNotes] = useState<ShowMiscNoteDTO[]>([]);
  const [categorizedNotes, setCategorizedNotes] = useState<ShowCategorizedNoteDTO[]>([]);

  // ---- Checklists V2-B: show checklists, filtered to the viewer's items ----
  // The viewer's team_member id comes as a PROP (not useAuth): this component
  // is rendered in tests without an AuthProvider, and the parent already holds
  // the session user. Null/absent = account not linked → non-editor sees none.
  const myMemberId = myMemberIdProp || null;
  const showIdForChecklists = realId && form.band_id ? String(realId) : null;
  const [myChecklists, setMyChecklists] = useState<ChecklistDTO[]>([]);
  useEffect(() => {
    if (!showIdForChecklists || !form.band_id) { setMyChecklists([]); return; }
    let cancelled = false;
    apiGetShowChecklists(form.band_id, showIdForChecklists)
      .then((rows) => { if (!cancelled) setMyChecklists(rows); })
      .catch(() => { if (!cancelled) setMyChecklists([]); });
    return () => { cancelled = true; };
  }, [showIdForChecklists, form.band_id]);
  // Personal daysheet rule (SPEC V2-B): a plain member sees ONLY the items
  // assigned to him; an editor+ (manager/owner) sees ALL items to run the day.
  const visibleChecklistItems = (() => {
    const pairs: { cl: ChecklistDTO; it: ChecklistItemDTO }[] = [];
    for (const cl of myChecklists) {
      for (const it of cl.items || []) {
        if (canWrite || (myMemberId && String(it.assignee_id || '') === String(myMemberId))) {
          pairs.push({ cl, it });
        }
      }
    }
    return pairs;
  })();
  const canTickItem = (_cl: ChecklistDTO, it: ChecklistItemDTO) =>
    canWrite || (!!myMemberId && String(it.assignee_id || '') === String(myMemberId));
  const tickItem = async (cl: ChecklistDTO, it: ChecklistItemDTO) => {
    const nextDone = !it.done;
    setMyChecklists((prev) => prev.map((c) => (c.id === cl.id
      ? { ...c, items: (c.items || []).map((x) => (x.id === it.id ? { ...x, done: nextDone } : x)) }
      : c)));
    try {
      if (canWrite) {
        const next = (cl.items || []).map((x) => (x.id === it.id ? { ...x, done: nextDone } : x));
        await apiUpdateChecklist(String(form.band_id), String(cl.id), { items: next });
      } else {
        await apiSetMyChecklistItemDone(it.id, nextDone);
      }
      // Refresh to pick up done_by/done_at from the server.
      if (showIdForChecklists && form.band_id) {
        const rows = await apiGetShowChecklists(form.band_id, showIdForChecklists).catch(() => null);
        if (rows) setMyChecklists(rows);
      }
    } catch (e: any) {
      console.error(e);
      // Silent for the read-only daysheet: reload the truth instead.
      if (showIdForChecklists && form.band_id) {
        const rows = await apiGetShowChecklists(form.band_id, showIdForChecklists).catch(() => null);
        if (rows) setMyChecklists(rows);
      }
    }
  };

  const load = useCallback(async () => {
    if (!realId) return;
    const r = await apiGetShowRoadmap(realId).catch(() => []);
    setRoadmap([...r].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    const c = await apiGetShowContacts(realId).catch(() => []);
    setContacts([...c].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    const d = await apiGetShowDistances(realId).catch(() => []);
    setDistances([...d].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    const f = await apiGetShowFlights(realId).catch(() => []);
    setFlights([...f].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    setTechNotes((await apiGetShowTechNotes(realId).catch(() => [])) as ShowTechNoteDTO[]);
    setMiscNotes((await apiGetShowMiscNotes(realId).catch(() => [])) as ShowMiscNoteDTO[]);
    setCategorizedNotes((await apiGetShowCategorizedNotes(realId).catch(() => [])) as ShowCategorizedNoteDTO[]);
  }, [realId]);

  useEffect(() => { void load(); }, [load]);

  // ---- Section title resolution (same source as the PDF: layout override, else i18n) ----
  const sectionTitle = (id: DaysheetSectionId): string => {
    const sec = layout?.sections.find((s) => s.id === id);
    if (sec?.title && sec.title.trim()) return sec.title.trim();
    // The header line prints NO heading of its own — same as the PDF, whose
    // SECTION_HEADINGS.header is empty. `pdf.ds.section.header` does not even
    // exist as an i18n key, so the viewer used to print that raw key as an <h4>.
    if (id === 'header') return '';
    return t(`pdf.ds.section.${id}`);
  };

  // Free-text fields (« ＋ Add text » in the layout editor) are REAL content:
  // the PDF prints them, so a section that only carries one must stay on screen.
  const sectionFreeText = (
    sec: { fields?: { id: string; enabled?: boolean; textContent?: string }[] },
  ): string[] =>
    (sec.fields ?? [])
      .filter((f) => f.enabled !== false && (f.id === 'text' || f.id.endsWith('.text')))
      .map((f) => (f.textContent ?? '').trim())
      .filter(Boolean);

  // ---- Renderers (each returns JSX or null when the section has no data) ----
  const renderHeader = () => (
    <div className="daysheet-viewer-header">
      <h3 style={{ margin: 0 }}>{bandName || form.band_name || 'Show'}</h3>
      <div className="daysheet-viewer-subtitle">{form.name || 'Show'}</div>
      <div className="daysheet-viewer-meta">
        <span>{t('pdf.ds.date', { value: form.date ? new Date(form.date).toLocaleDateString() : 'TBD' })}</span>
        {/* La salle n'est affichée que si elle est spécifiée (jour de voyage / studio / off). */}
        {(venue?.name || form.venue_name || '').trim() ? (
          <span>{t('pdf.ds.venue', { value: (venue?.name || form.venue_name || '').trim() })}</span>
        ) : null}
        <span>{t('pdf.ds.status', { value: form.status || t('pdf.ds.status.scheduled') })}</span>
        <span>{t('pdf.ds.type', { value: form.event_type || 'show' })}</span>
        {/* Transport is screen-only (option B): shown here in the header, but
            deliberately NOT in the printed daysheet (see TRANSPORT_LABEL note). */}
        {form.transport ? (
          <span>{t('showDetail.transport')}: {TRANSPORT_LABEL[form.transport] || form.transport}</span>
        ) : null}
      </div>
    </div>
  );

  const renderGeneralInfo = () => {
    // PROFIL PAR TYPE (2026-10-17, lib/showTypes.ts) : doors / running order /
    // stage name / guests n'ont aucun sens sur un travel day / day off / studio —
    // masqués dans le PDF aussi, valeurs intactes.
    const dsHidden = new Set(showTypeProfile(form.event_type).daySheetHidden || []);
    const pairs: { label: string; value?: string }[] = [];
    if (form.welcome_note) pairs.push({ label: t('pdf.ds.welcome'), value: form.welcome_note });
    if (form.timezone) pairs.push({ label: t('pdf.ds.timezone'), value: form.timezone });
    if (form.door_time && !dsHidden.has('doors')) pairs.push({ label: t('pdf.ds.doors'), value: form.door_time });
    if (form.electricity) pairs.push({ label: t('pdf.ds.electricity'), value: form.electricity });
    if (form.wifi_venue) pairs.push({ label: t('pdf.ds.wifi'), value: form.wifi_venue });
    if (form.currency_note) pairs.push({ label: t('pdf.ds.currency'), value: form.currency_note });
    if (form.weather) pairs.push({ label: t('pdf.ds.weather'), value: form.weather });
    if (form.currency_rate) pairs.push({ label: t('pdf.ds.rate'), value: form.currency_rate });
    if (form.luggage_notes) pairs.push({ label: t('pdf.ds.luggage'), value: form.luggage_notes });
    if (form.duration_minutes) pairs.push({ label: t('pdf.ds.duration'), value: t('pdf.ds.durationMinutes', { count: form.duration_minutes }) });
    if (form.stage_size) pairs.push({ label: t('pdf.ds.stage'), value: form.stage_size });
    if (form.stage_name && !dsHidden.has('stageName')) pairs.push({ label: t('pdf.ds.stageName'), value: form.stage_name });
    if (form.artist_pass) pairs.push({ label: t('pdf.ds.artistPass'), value: form.artist_pass });
    if (form.passport_reminder) pairs.push({ label: t('pdf.ds.passport'), value: form.passport_reminder });
    if (pairs.length === 0) return null;
    return (
      <div className="daysheet-viewer-grid">
        {pairs.map((p) => (
          <div className="daysheet-viewer-item" key={p.label}>
            <span className="daysheet-viewer-label">{p.label}</span>
            <span className="daysheet-viewer-value">{p.value || ''}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderVenue = () => {
    if (!venue) return null;
    const v = venue || {};
    const cityZip = [v.city, v.zip].filter(Boolean).join(' / ');
    const pairs: { label: string; value?: string }[] = [];
    if (v.name) pairs.push({ label: t('pdf.ds.venueInfo.name'), value: v.name });
    if (v.address) pairs.push({ label: t('pdf.ds.venueInfo.address'), value: v.address });
    if (cityZip) pairs.push({ label: t('pdf.ds.venueInfo.cityZip'), value: cityZip });
    if (v.country) pairs.push({ label: t('pdf.ds.venueInfo.country'), value: v.country });
    if (v.phone) pairs.push({ label: t('pdf.ds.venueInfo.phone'), value: v.phone });
    if (v.email) pairs.push({ label: t('pdf.ds.venueInfo.email'), value: v.email });
    if (v.website) pairs.push({ label: t('pdf.ds.venueInfo.website'), value: v.website });
    if (v.parking) pairs.push({ label: t('pdf.ds.venueInfo.parking'), value: v.parking });
    if (v.electricity) pairs.push({ label: t('pdf.ds.venueInfo.electricity'), value: v.electricity });
    if (v.timezone) pairs.push({ label: t('pdf.ds.venueInfo.timezone'), value: v.timezone });
    if (pairs.length === 0) return null;
    return (
      <div className="daysheet-viewer-grid">
        {pairs.map((p) => (
          <div className="daysheet-viewer-item" key={p.label}>
            <span className="daysheet-viewer-label">{p.label}</span>
            <span className="daysheet-viewer-value">{p.value || ''}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderContacts = () => {
    if (contacts.length === 0) return null;
    return (
      <div className="table-wrapper">
        <table className="daysheet-viewer-table tbl-dense">
          <thead>
            <tr>
              <th>{t('pdf.ds.col.role')}</th>
              <th>{t('pdf.ds.col.name')}</th>
              <th>{t('pdf.ds.col.phone')}</th>
              <th>{t('pdf.ds.col.email')}</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id}>
                <td>{c.role || ''}</td>
                <td>{c.name || ''}</td>
                <td>{c.phone || ''}</td>
                <td>{c.email || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderHotel = () => {
    if (accommodations.length === 0) return null;
    return (
      <div className="table-wrapper">
        <table className="daysheet-viewer-table tbl-dense">
          <thead>
            <tr>
              <th>{t('pdf.ds.col.hotel')}</th>
              <th>{t('pdf.ds.col.address')}</th>
              <th>{t('pdf.ds.col.phone')}</th>
              <th>{t('pdf.ds.col.conf')}</th>
            </tr>
          </thead>
          <tbody>
            {accommodations.map((a) => (
              <tr key={a.id}>
                <td>{a.hotel_name || ''}</td>
                <td>{[a.address, a.zip, a.city, a.country].filter(Boolean).join(', ') || ''}</td>
                <td>{a.phone || ''}</td>
                <td>{a.confirmation_number || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderVenuePhoto = () => {
    const dataUrl = (venuePhoto?.dataUrl || '').trim();
    if (!dataUrl) return null; // pas de photo ⇒ rien (aucun cadre vide)
    const caption = (venuePhoto?.caption || '').trim();
    return (
      <div style={{ textAlign: 'center' }}>
        <img src={dataUrl} alt={caption || t('pdf.ds.section.venue-photo')} style={{ width: '100%', maxWidth: 640, height: 'auto', borderRadius: 4 }} />
        {caption && (
          <p className="text-dim" style={{ fontSize: 'var(--font-sm)', fontStyle: 'italic', margin: '6px 0 0' }}>{caption}</p>
        )}
      </div>
    );
  };

  // TRAVEL (2026-11-03) — même règle que le PDF : la section n'apparaît que si la
  // date porte un trajet (from / to / moyen). Un jour de concert ne l'affiche pas.
  const renderTravel = () => {
    const from = String(form.from_city || '').trim();
    const to = String(form.to_city || '').trim();
    const meansKey = String(form.transport || '').trim().toLowerCase();
    const means = meansKey ? t(`pdf.ds.transport.${meansKey}`) : '';
    // Un trajet, pas un simple moyen de transport (parité PDF).
    if (!from && !to) return null;
    return (
      <div className="table-wrapper">
        <table className="daysheet-viewer-table tbl-dense">
          <thead>
            <tr>
              <th>{t('pdf.ds.col.from')}</th>
              <th>{t('pdf.ds.col.to')}</th>
              <th>{t('pdf.ds.col.means')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{from || '—'}</td>
              <td>{to || '—'}</td>
              <td>{means || '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderBandCrew = () => {
    const rows = (people || []).filter((p) => (p?.name || '').trim());
    if (rows.length === 0) return null;
    return (
      <div className="table-wrapper">
        <table className="daysheet-viewer-table tbl-dense">
          <thead>
            <tr>
              <th>{t('pdf.ds.col.name')}</th>
              <th>{t('pdf.ds.col.position')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={`${p.name}-${i}`}>
                <td>{p.name}</td>
                <td>{p.position || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRooming = () => {
    if (rooming.length === 0) return null;
    return (
      <div className="table-wrapper">
        <table className="daysheet-viewer-table tbl-dense">
          <thead>
            <tr>
              <th>{t('pdf.ds.col.name')}</th>
              <th>{t('pdf.ds.col.room')}</th>
              <th>{t('pdf.ds.col.hotel')}</th>
            </tr>
          </thead>
          <tbody>
            {rooming.map((rm) => {
              const hotel = accommodations.find((a) => a.id === rm.accommodation_id);
              const member = members.find((m) => String(m.id) === String(rm.team_member_id));
              return (
                <tr key={rm.id}>
                  <td>{member ? (member.officialName || member.name) : (rm.role_at_date || '')}</td>
                  <td>{rm.room_number || (rm.room_comment || '')}</td>
                  <td>{hotel?.hotel_name || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRunOfShow = () => {
    // Empty ⇒ invisible (2026-10-17) : plus de note « No schedule items yet. »
    // sur l'écran, la section disparaît avec son titre.
    if (roadmap.length === 0) return null;
    return (
      <div className="table-wrapper">
        <table className="daysheet-viewer-table tbl-dense">
          <thead>
            <tr>
              <th>{t('pdf.ds.col.time')}</th>
              <th>{t('pdf.ds.col.activity')}</th>
              <th>{t('pdf.ds.col.responsible')}</th>
            </tr>
          </thead>
          <tbody>
            {roadmap.map((it) => (
              <tr key={it.id}>
                <td>{[it.time_from, it.time_to].filter(Boolean).join(' – ') || (it as any).time || ''}</td>
                <td>{it.activity}</td>
                <td>{it.responsible || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderGuestlist = () => {
    // PROFIL PAR TYPE (2026-10-17) : pas de guest list sur un travel day / day off / studio.
    if (showTypeProfile(form.event_type).daySheetHidden?.includes('guests')) return null;
    if (guests.length === 0) return null;
    return (
      <div className="table-wrapper">
        <table className="daysheet-viewer-table tbl-dense">
          <thead>
            <tr>
              <th>{t('pdf.ds.col.name')}</th>
              <th>{t('pdf.ds.col.role')}</th>
              <th>{t('pdf.ds.col.status')}</th>
            </tr>
          </thead>
          <tbody>
            {guests.map((g) => (
              <tr key={g.id}>
                <td>{g.name}</td>
                <td>{g.role || ''}</td>
                <td>{g.check_in_status || 'pending'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderFlights = () => {
    if (flights.length === 0) return null;
    return (
      <>
        <div className="table-wrapper">
          <table className="daysheet-viewer-table tbl-dense">
            <thead>
              <tr>
                <th>{t('pdf.ds.col.people')}</th>
                <th>{t('pdf.ds.col.from')}</th>
                <th>{t('pdf.ds.col.to')}</th>
                <th>{t('pdf.ds.col.flight')}</th>
                <th>{t('pdf.ds.col.timeDuration')}</th>
              </tr>
            </thead>
            <tbody>
              {flights.map((f) => {
                let people: string[] = [];
                try { people = JSON.parse(f.member_ids || '[]'); } catch { people = []; }
                if (people.length === 0 && f.team_member_id) people = [f.team_member_id];
                const other = (f.person_name || '').trim();
                const allPeople = other ? [...people, ...other.split(',').map((s) => s.trim()).filter(Boolean)] : people;
                // Passagers triés A→Z, identité passeport d'abord (parité PDF).
                const names = sortPeopleNames(allPeople.map((i) => {
                  const m = members.find((mm) => String(mm.id) === String(i));
                  return m ? (m.officialName || m.name) : i;
                }));
                // TRAIN legs (SPEC-TRAIN-LEG-FIXES 2026-10-20): station fallback
                // in From/To + train details in the FLIGHT column (PDF parity).
                const isTrain = f.mode === 'train';
                const flightLabel = isTrain
                  ? [f.airline, f.flight_no, f.booking_ref, f.train_class, f.coach, f.seat, f.platform].filter((x) => (x || '').trim()).join(' ')
                  : [f.airline, f.flight_no, f.booking_ref].filter(Boolean).join(' ');
                const timeLabel = [f.departure, f.arrival].filter(Boolean).join(' – ');
                const withDuration = [timeLabel, f.duration].filter(Boolean).join(' · ');
                // IATA code next to the city — same rendering as the PDF (2026-10-18).
                const withIata = (city: string | undefined, iata: string | undefined): string =>
                  [city, iata ? `(${iata})` : ''].filter(Boolean).join(' ');
                const route = (city: string | undefined, iata: string | undefined, station?: string) =>
                  isTrain ? (station || city || '') : withIata(city, iata);
                return (
                  <tr key={f.id}>
                    <td>
                      {/* Une personne par ligne, comme dans le PDF (2026-11-03). */}
                      {names.length
                        ? names.map((n, ni) => (
                          <span key={`${n}-${ni}`} style={{ display: 'block' }}>{n}</span>
                        ))
                        : ''}
                    </td>
                    <td>{route(f.from_city, f.from_iata, f.from_station)}</td>
                    <td>{route(f.to_city, f.to_iata, f.to_station)}</td>
                    <td>{flightLabel || ''}</td>
                    <td>{withDuration || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {form.flight_notes ? <p className="daysheet-viewer-note">{form.flight_notes}</p> : null}
      </>
    );
  };

  const renderDistances = () => {
    if (distances.length === 0) return null;
    return (
      <div className="table-wrapper">
        <table className="daysheet-viewer-table tbl-dense">
          <thead>
            <tr>
              <th>{t('pdf.ds.col.from')}</th>
              <th>{t('pdf.ds.col.to')}</th>
              <th>{t('pdf.ds.col.mode')}</th>
              <th>{t('pdf.ds.col.distance')}</th>
              <th>{t('pdf.ds.col.duration')}</th>
            </tr>
          </thead>
          <tbody>
            {distances.map((d) => (
              <tr key={d.id}>
                <td>{d.from_label || ''}</td>
                <td>{d.to_label || ''}</td>
                <td>{d.mode || ''}</td>
                <td>{d.distance || ''}</td>
                <td>{d.duration || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderNotes = () => {
    if (!form.notes) return null;
    return <p className="daysheet-viewer-note">{form.notes}</p>;
  };

  const renderTechNotes = () => {
    if (techNotes.length === 0) return null;
    return (
      <div className="daysheet-viewer-list">
        {techNotes.map((n) => <p className="daysheet-viewer-note" key={String(n.id)}>{n.content || ''}</p>)}
      </div>
    );
  };

  const renderMiscNotes = () => {
    if (miscNotes.length === 0) return null;
    return (
      <div className="daysheet-viewer-list">
        {miscNotes.map((n) => (
          <div className="daysheet-viewer-note" key={String(n.id)}>
            {n.category ? <strong>{n.category}</strong> : null}
            {n.content || ''}
          </div>
        ))}
      </div>
    );
  };

  const renderCategorizedNotes = () => {
    if (categorizedNotes.length === 0) return null;
    return (
      <div className="daysheet-viewer-list">
        {categorizedNotes.map((n) => (
          <div className="daysheet-viewer-note" key={String(n.id)}>
            {n.title ? <strong>{n.title}</strong> : null}
            {n.category ? <em>{n.category}</em> : null}
            {n.content || ''}
          </div>
        ))}
      </div>
    );
  };

  // ---- Renderer registry: section id → read-only block (null = no data) ----
  const renderers: Record<string, () => React.ReactNode> = {
    header: renderHeader,
    travel: renderTravel,
    'general-info': renderGeneralInfo,
    venue: renderVenue,
    'venue-photo': renderVenuePhoto,
    'band-crew': renderBandCrew,
    contacts: renderContacts,
    hotel: renderHotel,
    rooming: renderRooming,
    'run-of-show': renderRunOfShow,
    guestlist: renderGuestlist,
    flights: renderFlights,
    distances: renderDistances,
    notes: renderNotes,
    'tech-notes': renderTechNotes,
    'misc-notes': renderMiscNotes,
    'categorized-notes': renderCategorizedNotes,
  };

  // ---- Iterate the SAME layout.sections the PDF consumes ----
  // The PDF (buildDaysheetDoc) re-merges the effective layout over the defaults
  // so a saved layout that predates a NEW section still exposes it. The viewer
  // applies the SAME merge — that is the single source of the section set.
  const effective = mergeDaysheetLayouts(createDefaultDaysheetLayout(), layout ?? null);
  const sections = effective.sections ?? [];
  // Reversal (2) 2026-10-17: the layout's `enabled` flag governs the SCREEN too.
  // One filter, one rule: a section disabled in the layout is invisible here just
  // as it is absent from the PDF. (The screen used to follow the order only.)
  const visible = sections.filter((s) => s.enabled);

  // U1 — chaque cellule d'un tableau dense porte le LIBELLÉ de sa colonne
  // (`data-l` posé depuis les `<th>`). Sous 720px le tableau devient une carte
  // par ligne (`td::before { content: attr(data-l) }`) : aucune colonne n'est
  // perdue et rien ne déborde. Aucune donnée ni contrôle n'est modifié.
  // U3 — le mécanisme est sorti dans `useDenseTableLabels` (partagé avec les
  // 18 autres écrans à tableau) ; le comportement de la day sheet est inchangé.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(rootRef);

  return (
    <div ref={rootRef} className="card daysheet-viewer" data-daysheet-viewer data-density="socle" style={{ marginBottom: 20 }}>
      <div className="daysheet-viewer-head">
        <h3 style={{ margin: 0 }}>{t('daysheetViewer.title')}</h3>
        {canWrite && (
          <button className="btn btn-primary" onClick={onEdit}>
            ✏️ {t('showDaySummary.edit')}
          </button>
        )}
      </div>
      {visible.map((sec) => {
        const id = sec.id as DaysheetSectionId;
        const render = renderers[id];
        const body = render ? render() : null;
        // Règle 2026-10-17 (1) — renverse l'invariant de 2026-10-11 : on ne
        // montre plus le VIDE (le filtre `s.enabled` ci-dessus porte, lui, la
        // règle 2026-10-17 (2) : le drapeau gouverne l'écran comme le PDF).
        // A section with no data (or no renderer) is not rendered at all: no
        // orphan heading, no placeholder, no leftover title. Only a section
        // that HAS content (its renderer produced a body, or it carries a
        // filled free-text field — `sectionFreeText` honours `f.enabled !==
        // false`) survives this guard.
        const freeText = sectionFreeText(sec);
        if (body == null && freeText.length === 0) return null;
        const title = sectionTitle(id);
        return (
          <section className="daysheet-viewer-section" data-daysheet-section={id} key={id}>
            {title ? <h4 className="daysheet-viewer-section-title">{withCardIcon('daysheet:' + id, title)}</h4> : null}
            {body}
            {freeText.map((txt, i) => (
              <p className="daysheet-viewer-note" key={`free-${i}`}>{txt}</p>
            ))}
          </section>
        );
      })}

      {/* Checklists V2-B — the PERSONAL daysheet block: a viewer sees the
          checklist items assigned to HIM (manager/editor sees all, to run the
          day). Ticking: editor+ via the checklist PUT; a plain member via the
          self-service route (/api/me), enforced server-side. */}
      {showIdForChecklists && myChecklists.length > 0 && visibleChecklistItems.length > 0 && (
        <section className="daysheet-viewer-section" data-daysheet-section="checklist">
          <h4 className="daysheet-viewer-section-title">{t('daysheetViewer.checklist')}</h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {visibleChecklistItems.map(({ cl, it }) => (
              <li key={`${cl.id}-${it.id}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0' }}>
                <input
                  type="checkbox"
                  checked={!!it.done}
                  disabled={!canTickItem(cl, it)}
                  onChange={() => void tickItem(cl, it)}
                  data-daysheet-checklist-item={it.id}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
                <span style={{ overflowWrap: 'anywhere', opacity: it.done ? 0.6 : 1 }}>
                  {it.text || '—'}
                  {canWrite && it.assignee_id ? (
                    <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}> → {members.find((m) => String(m.id) === String(it.assignee_id))?.name || it.assigned_to || '—'}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
